"use client"

import { useQueryClient } from "@tanstack/react-query"
import { FormattedMessage } from "react-intl"

import {
  Box,
  Button,
  MESSAGE_REQUESTS_FLAG_NAME,
  MessageChannel,
  MESSENGER_URI,
  P,
  useAcceptChat,
  useFlag,
  UserMessageChannel,
} from "@bullseye/components"
import { useRouter } from "@bullseye/navigation"

import {
  ApplicationModal,
  useApplicationModalContext,
} from "../../../../components"

type Props = {
  messageChannel: MessageChannel
  userMessageChannel: UserMessageChannel
  handleAcceptMessage: () => void
}

const formatName = (firstName: string, lastName?: string) => {
  if (!lastName) return firstName
  return `${firstName} ${lastName[0]}`
}

export function AcceptMessagePrompt({
  messageChannel,
  userMessageChannel,
  handleAcceptMessage,
}: Props) {
  const router = useRouter()
  const queryClient = useQueryClient()

  const messageRequestsFlagOn = useFlag(MESSAGE_REQUESTS_FLAG_NAME)

  const { openModal } = useApplicationModalContext()

  const formattedName = formatName(
    messageChannel.creator.first_name,
    messageChannel.creator.last_name,
  )
  const { accept, isLoading } = useAcceptChat(messageChannel)

  const disclaimer =
    userMessageChannel.shared_experience_title &&
    userMessageChannel.shared_experience_start_date ? (
      <FormattedMessage
        id={
          new Date(userMessageChannel.shared_experience_start_date) > new Date()
            ? "messenger.acceptMessage.sharedFutureExperienceDisclaimer"
            : "messenger.acceptMessage.sharedPastExperienceDisclaimer"
        }
        values={{ title: userMessageChannel.shared_experience_title }}
      />
    ) : (
      <FormattedMessage id="messenger.acceptMessage.noSharedExperienceDisclaimer" />
    )

  return (
    <Box className="flex items-center border-t border-solid border-gray-100 p-4 g-4">
      <Box className="flex items-center g-2">
        <P bold size="xl" className=" text-gray-950">
          <FormattedMessage
            id="messenger.acceptMessage"
            values={{ name: formattedName }}
          />
        </P>
        {messageRequestsFlagOn && (
          <P size="lg" className="pb-2 text-gray-400">
            {disclaimer}
          </P>
        )}
        <P className="text-center text-base">
          <FormattedMessage id="messenger.acceptMessage.description" />
        </P>
      </Box>
      <Box className="flex w-full flex-col gap-2 md:flex-row">
        <Box className="w-full md:w-1/3">
          <Button
            as="button"
            handlePress={async () => {
              await accept()
              await queryClient.invalidateQueries(["allChannels"])
              await queryClient.invalidateQueries([
                "message-channel",
                messageChannel.stream_channel_id,
              ])
              handleAcceptMessage()
            }}
            loading={isLoading}
            variant="primary"
          >
            <FormattedMessage id="messenger.acceptMessage.accept" />
          </Button>
        </Box>
        <Box className="flex w-full flex-row gap-2 md:w-2/3">
          <Button
            as="button"
            handlePress={() => {
              openModal(ApplicationModal.BlockUser, {
                userId: messageChannel.creator.id,
                onSuccess: () => {
                  router.push(MESSENGER_URI)
                },
              })
            }}
            variant="tertiary"
            className="flex-1"
            loading={isLoading}
          >
            <P bold className="text-red-600">
              <FormattedMessage id="messenger.modal.channelOptions.block" />
            </P>
          </Button>
          <Button
            as="button"
            handlePress={() => {
              openModal(ApplicationModal.Report, {
                contentType: "message_channel",
                contentId: messageChannel.id,
                blockUserId: messageChannel.creator.id,
              })
            }}
            variant="tertiary"
            className="flex-1"
            loading={isLoading}
          >
            <P bold className="text-red-600">
              <FormattedMessage id="messenger.modal.channelOptions.reportUser" />
            </P>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
